import { useState, useEffect } from 'react'
import { Card, Form, Row, Col, Button, Modal, Alert, Badge, InputGroup, Spinner } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'

const getNumberInputValue = (value, fallback = '') => (
  value === undefined || value === null ? fallback : value
)

const parseNumberInputValue = (value) => {
  if (value === '') return ''
  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

const TaskGroupManager = ({ taskManager }) => {
  const { 
    addTaskGroup, 
    updateTaskGroup, 
    deleteTaskGroup,
    getRuntimeData
  } = useAppStore()
  const runtimeData = getRuntimeData()
  const effectiveTaskGroups = runtimeData.taskGroups || {}
  
  const [showModal, setShowModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedTasks: [],
    taskConfigs: {}
  })
  const [tasks, setTasks] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [categories, setCategories] = useState([])
  const [loadingSelects, setLoadingSelects] = useState({})
  const [selectLoadErrors, setSelectLoadErrors] = useState({})
  const [checkboxGroupSearch, setCheckboxGroupSearch] = useState({})

  useEffect(() => {
    if (taskManager) {
      const allTasks = taskManager.getAllTasks()
      setTasks(allTasks)

      // 提取分类
      const categorySet = new Set(allTasks.map(task => task.category))
      setCategories([
        { value: 'all', label: '全部分类', count: allTasks.length },
        ...Array.from(categorySet).map(cat => ({
          value: cat,
          label: getCategoryLabel(cat),
          count: allTasks.filter(task => task.category === cat).length
        }))
      ])
    }
  }, [taskManager])

  const getCategoryLabel = (category) => {
    const categoryMap = {
      'daily': '活跃度',
      'resource': '资源收获',
      'activity': '活动',
      'reward': '奖励领取',
      'general': '通用类',
      'character': '武将类',
      'battle': '战斗类',
    }
    return categoryMap[category] || category
  }

  const getCategoryColor = (category) => {
    const colorMap = {
      'resource': 'success',
      'character': 'primary',
      'battle': 'danger',
      'reward': 'warning',
      'activity': 'info',
      'general': 'secondary',
      'example': 'dark'
    }
    return colorMap[category] || 'secondary'
  }

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || task.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleCreateGroup = () => {
    setEditingGroup(null)
    setFormData({
      name: '',
      description: '',
      selectedTasks: [],
      taskConfigs: {}
    })
    setShowModal(true)
  }

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setFormData({
      name: group.name,
      description: group.description,
      selectedTasks: group.selectedTasks,
      taskConfigs: group.taskConfigs || {}
    })
    setShowModal(true)
  }

  const handleSaveGroup = async () => {
    if (!formData.name.trim()) {
      alert('请输入任务组名称')
      return
    }

    if (formData.selectedTasks.length === 0) {
      alert('请至少选择一个任务')
      return
    }

    // 获取选中任务的配置
    const taskConfigs = {}
    for (const taskId of formData.selectedTasks) {
      const task = taskManager.getTask(taskId)
      if (task) {
        try {
          const uiConfig = await task.getUIConfig()
          if (uiConfig && uiConfig.controls && uiConfig.controls.length > 0) {
            // 使用当前表单中的配置，如果没有则使用默认值
            const currentConfig = formData.taskConfigs[taskId] || {}
            const defaultConfig = {}
            
            // 为每个控件设置默认值
            uiConfig.controls.forEach(control => {
              if (control.type === 'number-row') {
                const fields = control.fields || []
                fields.forEach(field => {
                  defaultConfig[field.key] = currentConfig[field.key] !== undefined ? currentConfig[field.key] : field.default
                })
              } else if (control.type === 'checkbox-number') {
                defaultConfig[control.key] = currentConfig[control.key] !== undefined
                  ? currentConfig[control.key]
                  : (control.defaultValue !== undefined ? control.defaultValue : false)
                defaultConfig[control.numberKey] = currentConfig[control.numberKey] !== undefined
                  ? currentConfig[control.numberKey]
                  : control.numberDefault
              } else if (currentConfig[control.key] !== undefined) {
                defaultConfig[control.key] = currentConfig[control.key]
              } else if (control.defaultValue !== undefined) {
                defaultConfig[control.key] = control.defaultValue
              } else {
                defaultConfig[control.key] = control.default
              }
            })
            
            taskConfigs[taskId] = defaultConfig
          }
        } catch (error) {
          console.error(`获取任务 ${task.name} 配置失败:`, error)
        }
      }
    }

    const groupData = {
      name: formData.name,
      description: formData.description,
      selectedTasks: formData.selectedTasks,
      taskConfigs,
      createdAt: editingGroup ? editingGroup.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    if (editingGroup) {
      updateTaskGroup(editingGroup.id, groupData)
    } else {
      addTaskGroup(groupData)
    }

    setShowModal(false)
  }

  const handleTaskToggle = (taskId) => {
    const newSelection = formData.selectedTasks.includes(taskId)
      ? formData.selectedTasks.filter(id => id !== taskId)
      : [...formData.selectedTasks, taskId]
    
    setFormData(prev => ({
      ...prev,
      selectedTasks: newSelection
    }))
  }

  const handleConfigUpdate = (taskId, configKey, value, extraConfig = {}) => {
    setFormData(prev => ({
      ...prev,
      taskConfigs: {
        ...prev.taskConfigs,
        [taskId]: {
          ...prev.taskConfigs[taskId],
          [configKey]: value,
          ...extraConfig
        }
      }
    }))
  }

  const ensureCurrentOption = (options, currentValue, currentText) => {
    const hintOption = {
      value: "__refresh_hint__",
      text: "如需更改，请点击右侧刷新加载完整列表",
      disabled: true
    }

    if (Array.isArray(currentValue)) {
      const missingOptions = currentValue
        .filter(value => !options.some(option => String(option.value) === String(value)))
        .map(value => ({ value, text: String(value), selected: true }))
      return [hintOption, ...missingOptions, ...options]
    }

    if (currentValue === undefined || currentValue === null || currentValue === '') {
      return [hintOption, ...options]
    }

    const hasCurrentOption = options.some(option => String(option.value) === String(currentValue))
    if (hasCurrentOption) return [hintOption, ...options]

    return [
      {
        value: currentValue,
        text: currentText || `已配置选项 (ID:${currentValue})`,
        selected: true
      },
      hintOption,
      ...options
    ]
  }

  const loadSelectOptions = async (task, control, taskConfig, updateControlOptions) => {
    if (!control.loadOptionsOnFocus || !control.loadOptionsMethod || typeof task[control.loadOptionsMethod] !== 'function') {
      return
    }

    const loadingKey = `${task.id}:${control.id || control.key}`
    if (loadingSelects[loadingKey]) return

    setLoadingSelects(prev => ({ ...prev, [loadingKey]: true }))
    setSelectLoadErrors(prev => {
      const next = { ...prev }
      delete next[loadingKey]
      return next
    })

    try {
      const accountKey = typeof task.getFirstAvailableAccountKey === 'function'
        ? task.getFirstAvailableAccountKey()
        : undefined
      const loadedOptions = await task[control.loadOptionsMethod](accountKey)
      const options = Array.isArray(loadedOptions) ? loadedOptions : []
      const currentValue = taskConfig[control.key]
      const currentText = control.persistTextKey ? taskConfig[control.persistTextKey] : ''
      updateControlOptions(control.id, ensureCurrentOption(options, currentValue, currentText))
    } catch (error) {
      console.error(`加载任务 ${task.name} 配置选项失败:`, error)
      setSelectLoadErrors(prev => ({
        ...prev,
        [loadingKey]: error.message || '加载失败'
      }))
    } finally {
      setLoadingSelects(prev => {
        const next = { ...prev }
        delete next[loadingKey]
        return next
      })
    }
  }

  const renderConfigControl = (task, control, updateControlOptions = () => {}) => {
    // 确保control对象有必要的属性
    if (!control || !control.key) {
      console.warn(`任务 ${task.name} 的配置项缺少必要属性:`, control)
      return (
        <div key={Math.random()} className="mb-2">
          <small className="text-warning">⚠️ 配置项格式错误</small>
        </div>
      )
    }

    const currentValue = formData.taskConfigs[task.id]?.[control.key] !== undefined 
      ? formData.taskConfigs[task.id][control.key] 
      : (control.defaultValue !== undefined ? control.defaultValue : control.default)
    const taskConfig = formData.taskConfigs[task.id] || {}

    const controlId = control.id || `${task.id}_${control.key}`

    try {
      switch (control.type) {
        case 'interval':
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <div className="input-group input-group-sm">
                <Form.Control
                  type="number"
                  size="sm"
                  min={control.min ?? 1}
                  max={control.max ?? 120}
                  value={getNumberInputValue(currentValue, control.default ?? 60)}
                  onChange={(e) => handleConfigUpdate(task.id, control.key, parseNumberInputValue(e.target.value))}
                />
                <span className="input-group-text">分钟</span>
              </div>
            </Form.Group>
          )

        case 'select': {
          // 确保options存在且是数组
          if (!control.options || !Array.isArray(control.options)) {
            console.warn(`任务 ${task.name} 的选择配置项缺少options:`, control)
            return (
              <Form.Group key={controlId} className="mb-2">
                <Form.Label className="small">{control.label || control.key}</Form.Label>
                <div className="small text-warning">⚠️ 选项配置错误</div>
              </Form.Group>
            )
          }

          const loadingKey = `${task.id}:${control.id || control.key}`
          const selectOptions = Array.isArray(control.options) ? control.options : []
          const canRefresh = control.loadOptionsOnFocus && control.loadOptionsMethod
          const isLoading = Boolean(loadingSelects[loadingKey])

          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <InputGroup size="sm">
                <Form.Select
                  size="sm"
                  value={currentValue !== undefined ? String(currentValue) : selectOptions.find(opt => opt.selected)?.value || ''}
                  disabled={isLoading}
                  onChange={(e) => {
                    const selectedValue = e.target.value
                    if (selectedValue === '__refresh_hint__') return
                    let convertedValue = selectedValue
                    if (selectedValue === 'true') convertedValue = true
                    else if (selectedValue === 'false') convertedValue = false
                    else if (!isNaN(selectedValue) && selectedValue !== '') convertedValue = Number(selectedValue)

                    const selectedOption = selectOptions.find(option => String(option.value) === selectedValue)
                    const extraConfig = {}
                    if (control.persistTextKey) {
                      extraConfig[control.persistTextKey] = selectedOption?.text || ''
                    }
                    
                    handleConfigUpdate(task.id, control.key, convertedValue, extraConfig)
                  }}
                >
                  {isLoading && (
                    <option value={currentValue !== undefined ? String(currentValue) : ''}>
                      正在加载选项...
                    </option>
                  )}
                  {selectOptions.map((option, index) => (
                    <option key={option.value || index} value={option.value} disabled={option.disabled}>
                      {option.text || option.label || option.value}
                    </option>
                  ))}
                </Form.Select>
                {canRefresh && (
                  <Button
                    variant={selectLoadErrors[loadingKey] ? 'outline-warning' : 'outline-secondary'}
                    onClick={() => loadSelectOptions(task, control, taskConfig, updateControlOptions)}
                    disabled={isLoading}
                    title={selectLoadErrors[loadingKey] ? `加载失败：${selectLoadErrors[loadingKey]}` : '刷新选项'}
                  >
                    {isLoading ? <Spinner animation="border" size="sm" /> : '刷新'}
                  </Button>
                )}
              </InputGroup>
              {selectLoadErrors[loadingKey] && (
                <Form.Text className="text-warning">
                  动态列表加载失败，可点击“刷新”重试。
                </Form.Text>
              )}
            </Form.Group>
          )
        }

        case 'number':
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                min={control.min ?? 1}
                max={control.max ?? 999}
                value={getNumberInputValue(currentValue, control.default ?? 1)}
                onChange={(e) => handleConfigUpdate(task.id, control.key, parseNumberInputValue(e.target.value))}
              />
            </Form.Group>
          )

        case 'number-row':
          return (
            <Form.Group key={controlId} className="mb-2">
              {control.label && <Form.Label className="small">{control.label}</Form.Label>}
              <Row className="g-2">
                {(control.fields || []).map(field => {
                  const fieldValue = formData.taskConfigs[task.id]?.[field.key] !== undefined
                    ? formData.taskConfigs[task.id][field.key]
                    : field.default
                  return (
                    <Col key={field.key} xs={12} md={(control.fields || []).length > 1 ? 6 : 12}>
                      <Form.Label className="small text-muted">{field.label}</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        min={field.min ?? 1}
                        max={field.max ?? 999}
                        value={getNumberInputValue(fieldValue, 0)}
                        onChange={(e) => handleConfigUpdate(task.id, field.key, parseNumberInputValue(e.target.value))}
                      />
                    </Col>
                  )
                })}
              </Row>
            </Form.Group>
          )

        case 'checkbox-number': {
          const numberValue = formData.taskConfigs[task.id]?.[control.numberKey] !== undefined
            ? formData.taskConfigs[task.id][control.numberKey]
            : control.numberDefault
          return (
            <Form.Group key={controlId} className="mb-2">
              <div className="d-flex align-items-end gap-2">
                <Form.Check
                  type="checkbox"
                  size="sm"
                  id={controlId}
                  label={control.label || control.key}
                  checked={currentValue !== undefined ? Boolean(currentValue) : Boolean(control.defaultValue)}
                  onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.checked)}
                  className="flex-grow-1 pb-1"
                />
                <div style={{ width: '96px' }}>
                  <Form.Label className="small text-muted">{control.numberLabel || '数值'}</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={control.min ?? 1}
                    max={control.max ?? 999}
                    value={getNumberInputValue(numberValue, 0)}
                    onChange={(e) => handleConfigUpdate(task.id, control.numberKey, parseNumberInputValue(e.target.value))}
                  />
                </div>
              </div>
            </Form.Group>
          )
        }

        case 'text':
        case 'password':
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <Form.Control
                type={control.type === 'password' ? 'password' : 'text'}
                size="sm"
                value={currentValue || control.default || ''}
                onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.value)}
                placeholder={control.placeholder}
              />
            </Form.Group>
          )

        case 'textarea':
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <Form.Control
                as="textarea"
                rows={control.rows || 2}
                size="sm"
                value={currentValue || control.default || ''}
                onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.value)}
                placeholder={control.placeholder}
              />
            </Form.Group>
          )

        case 'time':
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <Form.Control
                type="time"
                size="sm"
                value={currentValue || control.default || ''}
                onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.value)}
                step={control.step || 60}
              />
            </Form.Group>
          )

        case 'switch':
        case 'checkbox':
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Check
                type="checkbox"
                size="sm"
                label={control.label || control.key}
                checked={currentValue !== undefined ? Boolean(currentValue) : Boolean(control.defaultValue ?? control.default)}
                onChange={(e) => handleConfigUpdate(task.id, control.key, e.target.checked)}
              />
            </Form.Group>
          )

        case 'checkbox-group': {
          // 确保options存在且是数组
          if (!control.options || !Array.isArray(control.options)) {
            console.warn(`任务 ${task.name} 的多选配置项缺少options:`, control)
            return (
              <Form.Group key={controlId} className="mb-2">
                <Form.Label className="small">{control.label || control.key}</Form.Label>
                <div className="small text-warning">⚠️ 选项配置错误</div>
              </Form.Group>
            )
          }

          const loadingKey = `${task.id}:${control.id || control.key}`
          const canRefresh = control.loadOptionsOnFocus && control.loadOptionsMethod
          const isLoading = Boolean(loadingSelects[loadingKey])
          const selectedValues = Array.isArray(currentValue) ? currentValue : (control.default || [])
          const searchValue = checkboxGroupSearch[loadingKey] || ''
          const options = Array.isArray(control.options) ? control.options : []
          const selectedSet = new Set(selectedValues.map(value => String(value)))
          const filteredOptions = options.filter(option => {
            if (option.disabled) return true
            if (selectedSet.has(String(option.value))) return true
            if (!control.searchable || !searchValue.trim()) return true
            const text = [
              option.text,
              option.label,
              option.value,
              option.searchText
            ].filter(Boolean).join(" ").toLowerCase()
            return text.includes(searchValue.trim().toLowerCase())
          })
          return (
            <Form.Group key={controlId} className="mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label className="small mb-0">{control.label || control.key}</Form.Label>
                {canRefresh && (
                  <Button
                    variant={selectLoadErrors[loadingKey] ? 'outline-warning' : 'outline-secondary'}
                    size="sm"
                    onClick={() => loadSelectOptions(task, control, taskConfig, updateControlOptions)}
                    disabled={isLoading}
                    title={selectLoadErrors[loadingKey] ? `加载失败：${selectLoadErrors[loadingKey]}` : '刷新选项'}
                  >
                    {isLoading ? <Spinner animation="border" size="sm" /> : '刷新'}
                  </Button>
                )}
              </div>
              {control.searchable && (
                <Form.Control
                  type="search"
                  size="sm"
                  className="mt-2"
                  value={searchValue}
                  onChange={(e) => setCheckboxGroupSearch(prev => ({
                    ...prev,
                    [loadingKey]: e.target.value
                  }))}
                  placeholder="搜索武将名"
                />
              )}
              <div
                className="mt-1"
                style={control.maxHeight ? { maxHeight: `${control.maxHeight}px`, overflowY: 'auto' } : undefined}
              >
                {isLoading && <Form.Text className="text-muted">正在加载选项...</Form.Text>}
                {filteredOptions.map((option, index) => {
                  // 为每个选项生成唯一的ID，包含任务ID避免冲突
                  const uniqueOptionId = `${task.id}-${control.key}-${option.value || index}`
                  return (
                    <Form.Check
                      key={uniqueOptionId}
                      type="checkbox"
                      size="sm"
                      id={uniqueOptionId}
                      label={option.text || option.label || option.value}
                      checked={selectedValues.includes(option.value)}
                      disabled={option.disabled}
                      onChange={(e) => {
                        if (option.disabled) return
                        let newValues
                        if (e.target.checked) {
                          // 添加选项
                          newValues = [...selectedValues, option.value]
                        } else {
                          // 移除选项
                          newValues = selectedValues.filter(v => v !== option.value)
                        }
                        handleConfigUpdate(task.id, control.key, newValues)
                      }}
                      className="mb-1"
                    />
                  )
                })}
                {!isLoading && filteredOptions.length === 0 && (
                  <Form.Text className="text-muted">没有匹配的选项</Form.Text>
                )}
              </div>
              {selectLoadErrors[loadingKey] && (
                <Form.Text className="text-warning">
                  动态列表加载失败，可点击“刷新”重试。
                </Form.Text>
              )}
            </Form.Group>
          )
        }

        case 'stage-strategies': {
          const defaultPlans = Array.isArray(control.default) ? control.default : []
          const configuredPlans = Array.isArray(currentValue) ? currentValue : []
          const fallbackByPoint = new Map(defaultPlans.map(plan => [Number(plan.point), plan]))
          const configuredByPoint = new Map(configuredPlans.map(plan => [Number(plan?.point), plan]))
          const plans = Array.from({ length: 9 }, (_, index) => {
            const point = index + 1
            return {
              ...(fallbackByPoint.get(point) || {}),
              ...(configuredByPoint.get(point) || {}),
              point
            }
          })
          const updatePlan = (point, key, value) => {
            handleConfigUpdate(task.id, control.key, plans.map(plan => (
              plan.point === point ? { ...plan, [key]: value } : { ...plan }
            )))
          }

          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              {control.description && (
                <Form.Text className="d-block text-muted mb-2">{control.description}</Form.Text>
              )}
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">关卡</th>
                      <th scope="col" style={{ minWidth: '140px' }}>阵法</th>
                      <th scope="col" style={{ minWidth: '100px' }}>方案</th>
                      <th scope="col" style={{ minWidth: '120px' }}>失败重试</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map(plan => (
                      <tr key={plan.point}>
                        <th scope="row">第{plan.point}关</th>
                        <td>
                          <Form.Select
                            size="sm"
                            aria-label={`第${plan.point}关阵法`}
                            value={String(plan.matrixMid ?? '')}
                            onChange={(e) => updatePlan(plan.point, 'matrixMid', Number(e.target.value))}
                          >
                            {(control.matrixOptions || []).map(option => (
                              <option key={option.value} value={option.value}>{option.text}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            aria-label={`第${plan.point}关阵法方案`}
                            value={String(plan.matrixCase ?? '')}
                            onChange={(e) => updatePlan(plan.point, 'matrixCase', Number(e.target.value))}
                          >
                            {(control.caseOptions || []).map(option => (
                              <option key={option.value} value={option.value}>{option.text}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={1}
                            max={999}
                            aria-label={`第${plan.point}关失败重试次数`}
                            value={getNumberInputValue(plan.failureRetryLimit, 5)}
                            onChange={(e) => updatePlan(plan.point, 'failureRetryLimit', parseNumberInputValue(e.target.value))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Form.Group>
          )
        }
        case 'custom':
          if (control.html) {
            return (
              <div key={controlId} className="mb-2" dangerouslySetInnerHTML={{ __html: control.html }} />
            )
          }
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <div className="small text-muted">自定义配置项</div>
            </Form.Group>
          )

        default:
          console.warn(`任务 ${task.name} 包含未知的配置类型: ${control.type}`, control)
          return (
            <Form.Group key={controlId} className="mb-2">
              <Form.Label className="small">{control.label || control.key}</Form.Label>
              <div className="small text-warning">
                ⚠️ 未支持的配置类型: {control.type}
              </div>
              {/* 开发环境下显示调试信息 */}
              {import.meta.env.DEV && (
                <details className="mt-1">
                  <summary className="small text-muted" style={{cursor: 'pointer'}}>调试信息</summary>
                  <pre className="small text-muted mt-1" style={{fontSize: '10px'}}>
                    {JSON.stringify(control, null, 2)}
                  </pre>
                </details>
              )}
            </Form.Group>
          )
      }
    } catch (error) {
      console.error(`渲染任务 ${task.name} 的配置项 ${control.key} 时出错:`, error)
      return (
        <Form.Group key={controlId} className="mb-2">
          <Form.Label className="small">{control.label || control.key}</Form.Label>
          <div className="small text-danger">
            ⚠️ 配置项渲染失败: {error.message}
          </div>
        </Form.Group>
      )
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>任务组管理</h5>
        <Button variant="primary" onClick={handleCreateGroup}>
          创建任务组
        </Button>
      </div>

      {/* 任务组列表 */}
      <Row className="g-3">
        {Object.entries(effectiveTaskGroups).map(([groupId, group]) => (
          <Col key={groupId} md={6} lg={4}>
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{group.name}</h6>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1"
                    onClick={() => handleEditGroup({ ...group, id: groupId })}
                  >
                    编辑
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      if (confirm(`确定要删除任务组"${group.name}"吗？`)) {
                        deleteTaskGroup(groupId)
                      }
                    }}
                  >
                    删除
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <p className="text-muted small mb-2">{group.description}</p>
                <div className="mb-2">
                  <Badge bg="info">{group.selectedTasks.length} 个任务</Badge>
                </div>
                <div className="small text-muted">
                  创建时间: {new Date(group.createdAt).toLocaleString()}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {Object.keys(effectiveTaskGroups).length === 0 && (
        <Alert variant="info" className="text-center">
          暂无任务组，点击&quot;创建任务组&quot;开始创建
        </Alert>
      )}

      {/* 创建/编辑任务组模态框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? '编辑任务组' : '创建任务组'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>任务组名称</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="输入任务组名称"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>描述</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="输入任务组描述"
                />
              </Form.Group>

              {/* 任务选择 */}
              <h6>选择任务</h6>
              
              {/* 搜索和筛选 */}
              <Row className="mb-2 g-2">
                <Col>
                  <InputGroup size="sm">
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="搜索任务..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col>
                  <Form.Select
                    size="sm"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label} ({cat.count})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* 任务列表 */}
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {filteredTasks.map(task => (
                  <div
                    key={task.id}
                    className={`border rounded p-2 mb-1 d-flex align-items-center ${
                      formData.selectedTasks.includes(task.id) ? 'border-primary bg-primary bg-opacity-10' : 'border-light'
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleTaskToggle(task.id)}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={formData.selectedTasks.includes(task.id)}
                      onChange={() => handleTaskToggle(task.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="me-2"
                    />
                    <span className="me-2">{task.icon}</span>
                    <span className="flex-grow-1">{task.name}</span>
                    <Badge bg={getCategoryColor(task.category)} size="sm">
                      {getCategoryLabel(task.category)}
                    </Badge>
                  </div>
                ))}
              </div>
            </Col>

            {/* 任务配置 */}
            <Col md={6}>
              <h6>任务配置</h6>
              {formData.selectedTasks.length > 0 ? (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {formData.selectedTasks.map(taskId => {
                    const task = taskManager.getTask(taskId)
                    if (!task) return null

                    return (
                      <TaskConfigCard
                        key={taskId}
                        task={task}
                        taskConfigs={formData.taskConfigs}
                        onConfigUpdate={handleConfigUpdate}
                        renderConfigControl={renderConfigControl}
                      />
                    )
                  })}
                </div>
              ) : (
                <Alert variant="info">请先选择任务</Alert>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSaveGroup}>
            {editingGroup ? '更新' : '创建'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

// 任务配置卡片组件
const TaskConfigCard = ({ task, taskConfigs, renderConfigControl }) => {
  const [uiConfig, setUiConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true)
        setError(null)
        
        console.log(`开始加载任务 ${task.name} (${task.id}) 的配置`)
        
        // 检查task对象是否有getUIConfig方法
        if (typeof task.getUIConfig !== 'function') {
          throw new Error(`任务 ${task.name} 没有 getUIConfig 方法`)
        }
        
        const config = await task.getUIConfig({
          currentConfig: taskConfigs[task.id] || {}
        })
        console.log(`任务 ${task.name} 配置加载结果:`, config)
        
        // 验证配置格式
        if (config && typeof config === 'object') {
          setUiConfig(config)
        } else {
          console.warn(`任务 ${task.name} 返回的配置格式不正确:`, config)
          setUiConfig(null)
        }
        
      } catch (error) {
        console.error(`获取任务 ${task.name} 配置失败:`, error)
        setError(error.message)
        setUiConfig(null)
      } finally {
        setLoading(false)
      }
    }

    if (task) {
      loadConfig()
    }
  }, [task, taskConfigs])

  if (loading) {
    return (
      <Card className="mb-2">
        <Card.Header className="py-2">
          <div className="d-flex align-items-center">
            <span className="me-2">{task.icon}</span>
            <span className="small">{task.name}</span>
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          <div className="text-center small text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            加载配置中...
          </div>
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="mb-2 border-warning">
        <Card.Header className="py-2">
          <div className="d-flex align-items-center">
            <span className="me-2">{task.icon}</span>
            <span className="small">{task.name}</span>
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          <small className="text-warning">
            ⚠️ 配置加载失败: {error}
          </small>
        </Card.Body>
      </Card>
    )
  }

  // 检查配置是否有效
  const hasValidControls = uiConfig && 
                          uiConfig.controls && 
                          Array.isArray(uiConfig.controls) && 
                          uiConfig.controls.length > 0

  if (!hasValidControls) {
    return (
      <Card className="mb-2">
        <Card.Header className="py-2">
          <div className="d-flex align-items-center">
            <span className="me-2">{task.icon}</span>
            <span className="small">{task.name}</span>
          </div>
        </Card.Header>
        <Card.Body className="py-2">
          <small className="text-muted">此任务无需配置</small>
          {/* 调试信息 */}
          {import.meta.env.DEV && (
            <details className="mt-1">
              <summary className="small text-muted" style={{cursor: 'pointer'}}>调试信息</summary>
              <pre className="small text-muted mt-1" style={{fontSize: '10px'}}>
                {JSON.stringify(uiConfig, null, 2)}
              </pre>
            </details>
          )}
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card className="mb-2">
      <Card.Header className="py-2">
        <div className="d-flex align-items-center">
          <span className="me-2">{task.icon}</span>
          <span className="small">{task.name}</span>
          {/* 显示配置项数量 */}
          <Badge bg="secondary" className="ms-auto small">
            {uiConfig.controls.length} 项
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="py-2">
        {uiConfig.layout === "two-column" ? (
          <Row className="g-2">
            {uiConfig.controls.map((control, index) => (
              <Col key={control.id || control.key || index} xs={12} md={6}>
                {renderTaskConfigControl(task, control, index)}
              </Col>
            ))}
          </Row>
        ) : (
          uiConfig.controls.map((control, index) => renderTaskConfigControl(task, control, index))
        )}
      </Card.Body>
    </Card>
  )

  function renderTaskConfigControl(task, control, index) {
    try {
      const updateControlOptions = (controlId, options) => {
        setUiConfig(prev => ({
          ...prev,
          controls: prev.controls.map(item => (
            item.id === controlId || item.key === control.key ? { ...item, options } : item
          ))
        }))
      }
      return renderConfigControl(task, control, updateControlOptions)
    } catch (error) {
      console.error(`渲染任务 ${task.name} 的配置项 ${control.key} 失败:`, error)
      return (
        <div key={control.id || index} className="mb-2">
          <small className="text-danger">
            ⚠️ 配置项 &quot;{control.label || control.key}&quot; 渲染失败
          </small>
        </div>
      )
    }
  }
}

export default TaskGroupManager
